/**
 * The eraser, from the page's side.
 *
 * Two tiers, chosen per device:
 *
 *   LaMa on WebGPU   103 MB, about 3 s a fill on a laptop GPU. The one that
 *                    looks right around hands and clothing, which is where a
 *                    held phone always is. Safari 26 on iOS has WebGPU.
 *   MI-GAN on WASM   28 MB, about 1.3 s with threads. Weaker beside a hand —
 *                    it was trained on scenery — but good at scenery, which is
 *                    exactly the phone-on-a-shelf case, and fast anywhere.
 *
 * LaMa on plain WASM was measured at 12 s a fill on a desktop and would be
 * worse on a phone, so it is never chosen; a device without WebGPU gets MI-GAN.
 *
 * Each tier runs in its own worker (see inpaint.worker.ts for why a failed
 * start means a new worker rather than a retry).
 */
import { composite, type FinishReport } from '../erase/finish';
import { isAssetCached } from './assets';
import type { FromInpaint, InpaintBackend, InpaintModel, ToInpaint } from './inpaint.worker';

export interface EraserInfo {
  model: InpaintModel;
  backend: InpaintBackend;
  /** Model download size, for telling people before they spend it. */
  bytes: number;
}

export type EraserProgress = (loaded: number, total: number) => void;

const SIZES: Record<InpaintModel, number> = { lama: 103_400_000, migan: 28_100_000 };
const LAMA_EDGE = 512;

interface Live {
  worker: Worker;
  info: EraserInfo;
}

let live: Promise<Live> | null = null;
let progressListener: EraserProgress | null = null;
let nextId = 1;

async function hasWebGpu(): Promise<boolean> {
  const gpu = (navigator as Navigator & { gpu?: { requestAdapter(): Promise<unknown> } }).gpu;
  if (!gpu) return false;
  try {
    return (await gpu.requestAdapter()) !== null;
  } catch {
    return false;
  }
}

function spawn(model: InpaintModel, backend: InpaintBackend): Promise<Live> {
  const worker = new Worker(new URL('./inpaint.worker.ts', import.meta.url), { type: 'module', name: `eraser-${model}` });
  const threads = self.crossOriginIsolated ? Math.min(4, navigator.hardwareConcurrency || 2) : 1;
  return new Promise<Live>((resolve, reject) => {
    worker.onmessage = (e: MessageEvent<FromInpaint>) => {
      const msg = e.data;
      if (msg.type === 'progress') progressListener?.(msg.loaded, msg.total);
      else if (msg.type === 'ready') resolve({ worker, info: { model, backend, bytes: SIZES[model] } });
      else if (msg.type === 'failed') {
        worker.terminate();
        reject(new Error(msg.message));
      }
    };
    worker.onerror = (e) => {
      worker.terminate();
      reject(new Error(e.message || 'The eraser could not start.'));
    };
    const init: ToInpaint = { type: 'init', model, backend, threads };
    worker.postMessage(init);
  });
}

/**
 * `?eraser=fast` forces the fast tier. For support ("try the fast eraser" on a
 * phone whose GPU misbehaves) and for testing the path that devices without
 * WebGPU take.
 */
function forcedFast(): boolean {
  try {
    return new URLSearchParams(location.search).get('eraser') === 'fast';
  } catch {
    return false;
  }
}

async function start(): Promise<Live> {
  if (!forcedFast() && (await hasWebGpu())) {
    try {
      return await spawn('lama', 'webgpu');
    } catch {
      // A GPU that cannot compile LaMa is not an error worth showing anyone:
      // the fast tier is right behind it.
    }
  }
  return spawn('migan', 'wasm');
}

/** Download and compile the eraser. Safe to call repeatedly; failures can be retried. */
export function prepareEraser(onProgress?: EraserProgress): Promise<EraserInfo> {
  if (onProgress) progressListener = onProgress;
  live ??= start().catch((err) => {
    live = null;
    throw err;
  });
  return live.then((l) => l.info);
}

const FILES: Record<InpaintModel, string> = { lama: 'lama_512.onnx', migan: 'migan_pipeline_v2.onnx' };

/** Which tier this device would get, and whether it is downloaded, without downloading anything. */
export async function plannedEraser(): Promise<EraserInfo & { cached: boolean }> {
  const gpu = !forcedFast() && (await hasWebGpu());
  const model: InpaintModel = gpu ? 'lama' : 'migan';
  const cached = (await isAssetCached(FILES[model])) && (await isAssetCached('ort-wasm-simd-threaded.asyncify.wasm'));
  return { model, backend: gpu ? 'webgpu' : 'wasm', bytes: SIZES[model], cached };
}

/** True once the eraser has been loaded in this session. */
export function eraserLoaded(): boolean {
  return live !== null;
}

function runIn(l: Live, rgba: Uint8ClampedArray, hole: Uint8Array, w: number, h: number): Promise<Uint8ClampedArray> {
  const id = nextId++;
  return new Promise((resolve, reject) => {
    const previous = l.worker.onmessage;
    l.worker.onmessage = (e: MessageEvent<FromInpaint>) => {
      const msg = e.data;
      if (msg.type === 'done' && msg.id === id) {
        l.worker.onmessage = previous;
        resolve(msg.rgba);
      } else if (msg.type === 'failed' && msg.id === id) {
        l.worker.onmessage = previous;
        reject(new Error(msg.message));
      }
    };
    const run: ToInpaint = { type: 'run', id, rgba, hole, w, h };
    l.worker.postMessage(run, [rgba.buffer]);
  });
}

function canvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(w));
  c.height = Math.max(1, Math.round(h));
  return c;
}

function ctx(c: HTMLCanvasElement): CanvasRenderingContext2D {
  const x = c.getContext('2d', { willReadFrequently: true });
  if (!x) throw new Error('2D canvas context unavailable');
  return x;
}

export interface FillResult {
  canvas: HTMLCanvasElement;
  finish: FinishReport | null;
  ms: number;
  eraser: EraserInfo;
}

/**
 * Fill the masked pixels of `source`.
 *
 * Works on a crop around the hole — at least 512 square and three times the
 * hole — because both models only ever look at the neighbourhood, and a
 * twelve-megapixel tensor is memory an older phone does not have. LaMa takes
 * that crop at exactly 512; the result is scaled back and laid in with a
 * feathered edge and matched grain, and only the hole and its few-pixel
 * feather change. Everything else stays the camera's, byte for byte.
 *
 * @param hole  one byte per pixel of `source`; non-zero means "remove this"
 */
export async function fillHole(source: HTMLCanvasElement, hole: Uint8Array): Promise<FillResult> {
  const t0 = performance.now();
  await prepareEraser();
  const l = await live!;
  const { width: W, height: H } = source;

  let x0 = W;
  let y0 = H;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      if (!hole[y * W + x]) continue;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
    }
  }
  const out = canvas(W, H);
  const outCtx = ctx(out);
  outCtx.drawImage(source, 0, 0);
  if (x1 < 0) return { canvas: out, finish: null, ms: 0, eraser: l.info };

  const side = Math.min(Math.max(W, H), Math.max(LAMA_EDGE, 3 * Math.max(x1 - x0 + 1, y1 - y0 + 1)));
  const cw = Math.min(W, side);
  const ch = Math.min(H, side);
  const cx = Math.max(0, Math.min(W - cw, Math.round((x0 + x1) / 2 - cw / 2)));
  const cy = Math.max(0, Math.min(H - ch, Math.round((y0 + y1) / 2 - ch / 2)));

  const original = outCtx.getImageData(cx, cy, cw, ch);
  const cropHole = new Uint8Array(cw * ch);
  for (let y = 0; y < ch; y++) {
    for (let x = 0; x < cw; x++) cropHole[y * cw + x] = hole[(cy + y) * W + cx + x] ? 1 : 0;
  }

  let fill: Uint8ClampedArray;
  if (l.info.model === 'lama') {
    // Scale the crop to 512 for the model, and scale the answer back.
    const small = canvas(LAMA_EDGE, LAMA_EDGE);
    const smallCtx = ctx(small);
    smallCtx.imageSmoothingQuality = 'high';
    smallCtx.drawImage(source, cx, cy, cw, ch, 0, 0, LAMA_EDGE, LAMA_EDGE);
    const smallHole = new Uint8Array(LAMA_EDGE * LAMA_EDGE);
    for (let y = 0; y < LAMA_EDGE; y++) {
      // Dilating while shrinking: a hole pixel anywhere in the source block
      // marks the small pixel, so no sliver of phone survives the resample.
      const sy0 = Math.floor((y * ch) / LAMA_EDGE);
      const sy1 = Math.max(sy0 + 1, Math.ceil(((y + 1) * ch) / LAMA_EDGE));
      for (let x = 0; x < LAMA_EDGE; x++) {
        const sx0 = Math.floor((x * cw) / LAMA_EDGE);
        const sx1 = Math.max(sx0 + 1, Math.ceil(((x + 1) * cw) / LAMA_EDGE));
        let any = 0;
        for (let sy = sy0; sy < sy1 && !any; sy++) for (let sx = sx0; sx < sx1; sx++) if (cropHole[sy * cw + sx]) { any = 1; break; }
        smallHole[y * LAMA_EDGE + x] = any;
      }
    }
    const filled = await runIn(l, smallCtx.getImageData(0, 0, LAMA_EDGE, LAMA_EDGE).data, smallHole, LAMA_EDGE, LAMA_EDGE);
    const back512 = smallCtx.createImageData(LAMA_EDGE, LAMA_EDGE);
    back512.data.set(filled);
    smallCtx.putImageData(back512, 0, 0);
    const back = canvas(cw, ch);
    const backCtx = ctx(back);
    backCtx.imageSmoothingQuality = 'high';
    backCtx.drawImage(small, 0, 0, cw, ch);
    fill = backCtx.getImageData(0, 0, cw, ch).data;
  } else {
    fill = await runIn(l, new Uint8ClampedArray(original.data), cropHole, cw, ch);
  }

  const finish = composite(original.data, fill, cropHole, cw, ch);
  outCtx.putImageData(original, cx, cy);
  return { canvas: out, finish, ms: Math.round(performance.now() - t0), eraser: l.info };
}
